import { ApiAuthenticationFailedException } from '../errors'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'
import { asterAccountCategory } from './constants'
import {
  type SignAsterV3Payload,
  asterV3ChainParam,
  buildAsterV3QueryString,
  createAsterV3Nonce,
  normalizeAsterV3Auth,
} from './v3'

/**
 * Aster API用のクエリ文字列生成（URLエンコードなし、ソートなし）
 * Aster APIは署名計算時にパラメータのソートを行わない
 * パラメータは渡された順序のまま使用する
 */
const buildQueryStringNoEncode = (params?: Record<string, unknown>): string => {
  if (!params || Object.keys(params).length === 0) return ''
  return Object.entries(params)
    .map(([key, value]) => `${key}=${String(value)}`)
    .join('&')
}

/**
 * Aster API用のクエリ文字列生成（URLエンコードあり、ソートなし）
 * リクエスト送信時に使用
 */
const buildQueryStringNoSort = (params?: Record<string, unknown>): string => {
  if (!params || Object.keys(params).length === 0) return ''
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join('&')
}

/**
 * Aster v3 署名用に primitive 値だけを string に正規化する。
 * @param value - 正規化対象の値
 * @returns string 化された値
 */
const normalizeAsterV3RequestValue = (params: {
  key: string
  value: unknown
}): string | undefined => {
  const { key, value } = params

  if (value === undefined || value === null) {
    return undefined
  }

  if (Array.isArray(value)) {
    throw new Error(`Aster v3 does not support array parameters: ${key}`)
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value.toString()
  }

  throw new Error(`Unsupported Aster v3 parameter type for key: ${key}`)
}

const SPOT_BASE_URL = 'https://sapi.asterdex.com'
const FUTURES_BASE_URL = 'https://fapi.asterdex.com'
const RECV_WINDOW = 5000 // 5秒の有効期間

/**
 * Aster v3 の nonce エラー時に検出する文言一覧。
 */
const asterV3NonceErrorMessages = [
  'Nonce Expired',
  'duplicate request',
  'too old',
  'expired',
] as const

/**
 * Aster 認証ヘッダーを生成する。(v1 HMAC 用)
 */
const createV1AuthHeaders = async (params: {
  apiKey: string
  createHeaders?: () => Promise<Record<string, string>>
  method: 'GET' | 'POST'
}): Promise<Record<string, string>> => {
  const { apiKey, createHeaders, method } = params

  const overrideHeaders = createHeaders ? await createHeaders() : {}

  const headers: Record<string, string> = {
    'X-MBX-APIKEY': apiKey,
    ...overrideHeaders,
  }

  if (method === 'POST') {
    headers['Content-Type'] = 'application/x-www-form-urlencoded'
  }

  return headers
}

/**
 * Aster API クライアントを生成する。
 * `apiPassphrase` が設定されている場合は v3 認証、未設定の場合は v1 認証を利用する。
 * v3 では `signV3` (ウォレットなど外部署名者) に署名を委譲する。
 * @param params - クライアント生成条件
 * @param params.apiKey - v1 では API key、v3 では user address
 * @param params.apiPassphrase - v3 で利用する signer address
 * @param params.apiSecret - v1 では API secret (v3 では不要)
 * @param params.signV3 - v3 の EIP-712 署名を行う関数 (ウォレットなど)
 * @param params.noAuth - 認証不要エンドポイント用フラグ
 * @param params.createHeaders - 追加ヘッダー生成関数
 * @param params.proxy - プロキシ URL
 * @returns REST API クライアント
 */
export const createClient = ({
  apiKey,
  apiPassphrase,
  apiSecret,
  signV3,
  noAuth = false,
  createHeaders,
  proxy,
}: {
  /** v1 では API Key, v3 では user address */
  apiKey: string
  /** Aster v3 では signer address を指定する */
  apiPassphrase?: string
  /** v1 では API Secret (v3 では使用しない) */
  apiSecret?: string
  /** Aster v3 の EIP-712 署名関数 (ウォレットなど) */
  signV3?: SignAsterV3Payload
  /** 認証なしで実行する場合は noAuth を true に設定 */
  noAuth?: boolean
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) => {
  const useV3 = apiPassphrase !== undefined && apiPassphrase.length > 0

  return createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      // パスに応じてベースURLを選択
      const baseUrl = path.startsWith('/fapi')
        ? FUTURES_BASE_URL
        : SPOT_BASE_URL

      if (noAuth) {
        const queryString = buildQueryStringNoSort({
          ...query,
          ...body,
        })
        switch (method) {
          case 'GET': {
            return {
              url: `${baseUrl}${path}${queryString ? `?${queryString}` : ''}`,
              headers: createHeaders ? await createHeaders() : undefined,
            }
          }
          case 'POST': {
            return {
              url: `${baseUrl}${path}`,
              body: queryString,
              headers: createHeaders ? await createHeaders() : undefined,
            }
          }
          default: {
            throw new Error(`Unsupported method: ${method satisfies never}`)
          }
        }
      }

      if (useV3) {
        if (apiPassphrase === undefined) {
          throw new Error('apiPassphrase is required for Aster v3')
        }
        if (signV3 === undefined) {
          throw new Error('signV3 is required for Aster v3')
        }

        const v3Auth = normalizeAsterV3Auth({
          user: apiKey,
          signer: apiPassphrase,
        })
        const requestValues: Record<string, string> = {}
        for (const [key, value] of Object.entries(query ?? {})) {
          const normalizedValue = normalizeAsterV3RequestValue({
            key,
            value,
          })
          if (normalizedValue !== undefined) {
            requestValues[key] = normalizedValue
          }
        }
        for (const [key, value] of Object.entries(body ?? {})) {
          const normalizedValue = normalizeAsterV3RequestValue({
            key,
            value,
          })
          if (normalizedValue !== undefined) {
            requestValues[key] = normalizedValue
          }
        }
        // 公式 Python SDK の send_by_url() に合わせた挿入順:
        // business params → asterChain → user → signer → nonce
        const params = {
          ...requestValues,
          asterChain: asterV3ChainParam,
          user: v3Auth.user,
          signer: v3Auth.signer,
          nonce: createAsterV3Nonce(),
        }
        const queryString = buildAsterV3QueryString(params)
        const signature = await signV3(queryString)

        const overrideHeaders = createHeaders ? await createHeaders() : {}

        switch (method) {
          case 'GET': {
            return {
              url: `${baseUrl}${path}?${queryString}&signature=${signature}`,
              headers: overrideHeaders,
            }
          }
          case 'POST': {
            return {
              url: `${baseUrl}${path}`,
              body: `${queryString}&signature=${signature}`,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...overrideHeaders,
              },
            }
          }
          default: {
            throw new Error(`Unsupported method: ${method satisfies never}`)
          }
        }
      }

      // v1 HMAC
      if (apiSecret === undefined) {
        throw new Error('apiSecret is required for Aster v1')
      }

      const timestamp = Date.now().toString()
      const recvWindow = RECV_WINDOW.toString()

      const params = {
        ...query,
        ...body,
        recvWindow,
        timestamp,
      }

      // 署名計算用の文字列（エンコードなし）
      const dataString = buildQueryStringNoEncode(params)

      // HMAC-SHA256署名の生成
      const signature = await sign({
        algorithm: 'sha256',
        payload: dataString,
        secret: apiSecret,
        encoding: 'hex',
      })

      const headers = await createV1AuthHeaders({
        apiKey,
        createHeaders,
        method,
      })

      if (method === 'GET') {
        const queryWithSignature = buildQueryStringNoSort({
          ...params,
          signature,
        })
        return {
          url: `${baseUrl}${path}?${queryWithSignature}`,
          headers,
        }
      }
      const bodyWithSignature = buildQueryStringNoSort({ ...params, signature })
      return {
        url: `${baseUrl}${path}`,
        headers,
        body: bodyWithSignature,
      }
    },
    getDelayForRetry: ({ statusCode, errorMessage }) => {
      if (useV3) {
        if (
          statusCode === 400 &&
          asterV3NonceErrorMessages.some((message) =>
            errorMessage.includes(message),
          )
        ) {
          return 1000
        }
        if (statusCode === 401) {
          return 1000
        }
        if (statusCode === 418) {
          return undefined
        }
        if (statusCode === 429) {
          return 10_000
        }
        if (statusCode >= 500) {
          return 1000
        }
        return undefined
      }

      // v1
      if (statusCode === 400) {
        if (
          errorMessage.includes(
            'Timestamp for this request is outside of the recvWindow',
          ) ||
          errorMessage.includes(
            "Timestamp for this request was 1000ms ahead of the server's time",
          )
        ) {
          return 1000
        }
        if (errorMessage.includes('Invalid Api-Key')) {
          throw new ApiAuthenticationFailedException(asterAccountCategory)
        }
      }
      if (statusCode === 401) {
        if (errorMessage.includes('API-key format invalid')) {
          throw new ApiAuthenticationFailedException(asterAccountCategory)
        }
        if (
          errorMessage.includes(
            'Invalid API-key, IP, or permissions for action',
          )
        ) {
          return undefined
        }
        return 1000
      }
      if (statusCode === 418) {
        return undefined
      }
      if (statusCode === 429) {
        return 10_000
      }
      if (statusCode >= 500) {
        return 1000
      }
      return undefined
    },
    proxy,
  })
}
